import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard, currency } from "@/components/Primitives";
import { ChevronLeft, TrendingUp, DollarSign, Clock, CheckSquare, FileDown } from "lucide-react";

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-US", { month: "short", year: "numeric" });
};

function BarRow({ label, count, max, tone }) {
  const pct = max ? (count / max) * 100 : 0;
  return (
    <div data-testid="report-bar-row" className="flex items-center gap-3 py-1.5">
      <span className="w-32 shrink-0 text-sm text-[#575755] capitalize">{label}</span>
      <div className="flex-1 h-2.5 bg-[#F0EFEC] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: tone }} />
      </div>
      <span className="w-8 text-right text-sm font-semibold tabular-nums text-[#0A0A0A]">{count}</span>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white border border-[#E5E5E2] rounded-sm">
      <div className="px-6 py-4 border-b border-[#E5E5E2]"><h3 className="font-serif text-xl">{title}</h3></div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export default function TransactionReports() {
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => { api.get("/tm/reports").then((res) => setR(res.data)).catch(() => {}); }, []);

  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get("/tm/reports.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "transaction-pipeline-report.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPdfLoading(false); }
  };

  const statusMax = r ? Math.max(1, ...Object.values(r.status_counts)) : 1;
  const sideMax = r ? Math.max(1, ...Object.values(r.side_counts)) : 1;
  const typeMax = r ? Math.max(1, ...r.type_breakdown.map((x) => x.count)) : 1;
  const monthlyMax = r ? Math.max(1, ...r.monthly_pipeline.map((x) => x.value)) : 1;

  return (
    <div data-testid="tm-reports-page">
      <button data-testid="reports-back-btn" onClick={() => navigate("/transactions")} className="inline-flex items-center gap-1.5 text-sm text-[#575755] hover:text-[#1F3C88] mb-4 transition-colors">
        <ChevronLeft className="w-4 h-4" /> Back to Transactions
      </button>
      <PageHeader title="Transaction Reports" subtitle="A summarized view of pipeline value, deal velocity and workload across every transaction."
        action={<button data-testid="reports-export-pdf-btn" onClick={exportPdf} disabled={pdfLoading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312] transition-colors duration-200 disabled:opacity-60">
          <FileDown className="w-4 h-4" /> {pdfLoading ? "Preparing…" : "Export PDF"}
        </button>} />

      {r && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <StatCard index={0} label="Total Closed" value={r.cy_closed?.total ?? 0} sub={`${r.cy_closed?.year ?? ""} calendar year`} />
            <StatCard index={1} label="Seller Side" value={r.cy_closed?.seller ?? 0} sub="Closed this year" />
            <StatCard index={2} label="Buyer Side" value={r.cy_closed?.buyer ?? 0} sub="Closed this year" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard index={0} label="Pipeline Value" value={currency(r.pipeline_value)} sub={`${r.status_counts.active} active deals`} />
            <StatCard index={1} label="Closed Volume" value={currency(r.closed_volume)} sub={`${r.closed_count} closed deals`} />
            <StatCard index={2} label="Avg Days to Close" value={r.avg_days_to_close || "—"} sub="Contract to closing" />
            <StatCard index={3} label="Task Completion" value={`${r.task_completion_pct}%`} sub={`${r.tasks_overdue} overdue`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Panel title="Deals by Status">
              <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-[#8a8a86]"><TrendingUp className="w-4 h-4 text-[#F7941D]" /> {r.total} total transactions</div>
              <BarRow label="Active" count={r.status_counts.active} max={statusMax} tone="#F7941D" />
              <BarRow label="Closed" count={r.status_counts.closed} max={statusMax} tone="#1F3C88" />
              <BarRow label="Cancelled" count={r.status_counts.cancelled} max={statusMax} tone="#992B2B" />
            </Panel>

            <Panel title="Representation Side">
              <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-[#8a8a86]"><DollarSign className="w-4 h-4 text-[#F7941D]" /> Buyer vs seller vs dual</div>
              <BarRow label="Buyer" count={r.side_counts.buyer} max={sideMax} tone="#2E5C47" />
              <BarRow label="Seller" count={r.side_counts.seller} max={sideMax} tone="#1F3C88" />
              <BarRow label="Dual" count={r.side_counts.dual} max={sideMax} tone="#F7941D" />
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Panel title="Property Types">
              {r.type_breakdown.length === 0 ? <div className="text-sm text-[#a3a099]">No data yet.</div> :
                r.type_breakdown.map((x) => <BarRow key={x.label} label={x.label} count={x.count} max={typeMax} tone="#1F3C88" />)}
            </Panel>

            <Panel title="Upcoming Closings by Month">
              <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-[#8a8a86]"><Clock className="w-4 h-4 text-[#F7941D]" /> Active pipeline forecast</div>
              {r.monthly_pipeline.length === 0 ? <div className="text-sm text-[#a3a099]">No upcoming closings scheduled.</div> :
                r.monthly_pipeline.map((m) => (
                  <div key={m.month} data-testid="report-month-row" className="flex items-center gap-3 py-1.5">
                    <span className="w-24 shrink-0 text-sm text-[#575755]">{monthLabel(m.month)}</span>
                    <div className="flex-1 h-2.5 bg-[#F0EFEC] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#F7941D] transition-all duration-500" style={{ width: `${(m.value / monthlyMax) * 100}%` }} />
                    </div>
                    <span className="w-14 text-right text-xs tabular-nums text-[#575755]">{m.count} · {currency(m.value)}</span>
                  </div>
                ))}
            </Panel>
          </div>

          <Panel title="Workload">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-[#2E5C47]" /><div><div className="text-2xl font-serif">{r.tasks_done}</div><div className="text-xs text-[#8a8a86]">Tasks completed</div></div></div>
              <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-[#F7941D]" /><div><div className="text-2xl font-serif">{r.tasks_total - r.tasks_done}</div><div className="text-xs text-[#8a8a86]">Tasks outstanding</div></div></div>
              <div className="flex items-center gap-3"><CheckSquare className="w-5 h-5 text-[#992B2B]" /><div><div className="text-2xl font-serif">{r.tasks_overdue}</div><div className="text-xs text-[#8a8a86]">Tasks overdue</div></div></div>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
